import { FilterIcon } from 'lucide-react';
import { FC, Fragment } from 'react';
import { FiltersDescriptionPart } from './FiltersDescriptionPart';
import { FiltersDescriptionSeparator } from './FiltersDescriptionSeparator';
import { type DescriptionActions, type DescriptionPart, useDescriptionFit } from './model/filtersDescription';

/** Пропсы строки описания действующего отбора */
type Props = {
  /** Части описания; `null` — фильтров нет, строка не показывается */
  parts: DescriptionPart[] | null;
  /** Управление отбором из описания */
  actions: DescriptionActions;
};

/**
 * Значок описания: он же держит отступ строки слева, выравнивающий её по значку поиска строки
 * управления под ней. Отступ на самом значке, а не на строке: отступ строки в её ширину не
 * входит, и умещение считалось бы по большей ширине, чем есть на самом деле.
 */
const LEAD_ICON_CLASS = 'mr-1.5 ml-2.5 size-4 shrink-0';

/** Описание действующего отбора списка диалогов над всеми контролами страницы */
export const FiltersDescription: FC<Props> = ({ parts, actions }) => {
  const { containerRef, measureRef, measureItems, visibleCount, hiddenCounts, valueLimits } = useDescriptionFit(
    parts ?? [],
  );

  if (!parts?.length) return null;

  const visibleParts = parts.slice(0, visibleCount);
  const isClipped = visibleParts.length < parts.length;

  return (
    <div
      ref={containerRef}
      data-testid="dialogs-filters-description"
      className="relative flex w-full min-w-0 items-center overflow-hidden whitespace-nowrap text-sm text-muted-foreground"
    >
      {/* Тот же значок, каким DataGrid помечает фильтр в шапке колонки */}
      <FilterIcon className={LEAD_ICON_CLASS} aria-hidden />
      {visibleParts.map((part, index) => (
        <Fragment key={part.id}>
          <FiltersDescriptionPart
            part={part}
            hiddenCount={hiddenCounts[part.id] ?? 0}
            valueLimit={valueLimits[part.id] ?? null}
            actions={actions}
          />
          {index < visibleParts.length - 1 ? <FiltersDescriptionSeparator /> : null}
        </Fragment>
      ))}
      {/* Ушедший из строки хвост описания обозначен многоточием: строка не обрывается краем */}
      {isClipped ? <FiltersDescriptionSeparator isClipped testId="dialogs-filters-description-clipped" /> : null}
      {/* Слой замера стоит внутри строки, чтобы наследовать её шрифт: иначе снятые с него
          ширины разошлись бы с шириной показанного описания */}
      {measureItems ? (
        <div ref={measureRef} aria-hidden className="pointer-events-none invisible absolute top-0 left-0 flex">
          {measureItems.map((item) => (
            <div key={item.key} className="flex shrink-0 items-center">
              {item.kind === 'lead' ? <FilterIcon className={LEAD_ICON_CLASS} aria-hidden /> : null}
              {item.kind === 'separator' || item.kind === 'clipped' ? (
                <FiltersDescriptionSeparator isClipped={item.kind === 'clipped'} />
              ) : null}
              {item.kind === 'part' ? (
                <FiltersDescriptionPart
                  part={item.part}
                  hiddenCount={item.hiddenCount}
                  valueLimit={item.valueLimit}
                  actions={actions}
                />
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
